import cloneDeep from 'lodash/cloneDeep';

import {
    DateFormat,
    FileUploadType,
    ResidenceType,
    smileTypes,
} from '@webapp/account/src/resources/questions/questions';

import { DEFAULT_SMILE_PACK } from '@webapp/common/components/smile-image/smiles';
import { initialCol } from '@webapp/common/entities/question-col';
import { initialRow } from '@webapp/common/entities/question-row';
import { DateTimeType, TimeFormat } from '@webapp/common/lib/date';
import { BoolAnswerText, getColor } from '@webapp/common/lib/ui';
import type { FillerQuestion } from '@webapp/common/resources/entities/question';
import { initialQuestion, QuestionCategoryOrigin } from '@webapp/common/resources/entities/question';
import { PLACEHOLDER, TEXTS } from '@webapp/common/resources/fillers';
import {
    QuestionParamsCardSortingType,
    QuestionParamsModelAbType,
    QuestionParamsModelPairComparisonType,
} from '@webapp/common/resources/question';
import { AnswerType, DropdownType, QuestionType } from '@webapp/common/resources/survey';

export const ROW_CITY = {
    value: ResidenceType.CITY,
    order: 3,
};
export const ROW_REGION = {
    value: ResidenceType.REGION,
    order: 2,
};
export const ROW_COUNTRY = {
    value: ResidenceType.COUNTRY,
    order: 1,
};

export const DEFAULT_DISTRIBUTE_SCORE = 100;
export const DEFAULT_LONG_TEXT_MAX_LENGTH = 65000;
export const DEFAULT_SHORT_TEXT_MAX_LENGTH = 1000;

export const onAddQuestionOrRowFillers: { [key: string]: FillerQuestion } = {
    [QuestionType.TEST_FEW_OF_LIST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.TEST_FEW_OF_LIST,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value3: 0,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            totalScore: true,
            testScore: 1,
            testAnswerCorrectText: TEXTS.CORRECT_ANSWER,
            testAnswerIncorrectText: TEXTS.INCORRECT_ANSWER,
        },
    },
    [QuestionType.TEST_ONE_OF_LIST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.TEST_ONE_OF_LIST,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value3: 0,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            testAnswerCorrectText: TEXTS.CORRECT_ANSWER,
            testAnswerIncorrectText: TEXTS.INCORRECT_ANSWER,
        },
    },
    [QuestionType.TEST_TEXT]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.TEST_TEXT,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value3: 0,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            testScore: 1,
            testAnswerCorrectText: TEXTS.CORRECT_ANSWER,
            testAnswerIncorrectText: TEXTS.INCORRECT_ANSWER,
        },
    },
    [QuestionType.TEXT_BLOCK]: {
        ...cloneDeep(initialQuestion),
        name: '',
        type: QuestionType.TEXT_BLOCK,
        params: {
            ...cloneDeep(initialQuestion.params),
            name: false,
            help: null,
            timer: null,
            comment: null,
            required: null,
            imagePosition: 'by-height',
        },
    },
    [QuestionType.AGREEMENT]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.AGREEMENT,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value: [BoolAnswerText.TRUE, BoolAnswerText.FALSE][id - 1],
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            name: false,
            timer: null,
            comment: null,
            agreementLeftLabel: 'Согласен с политикой обработки',
            agreementRightLabel: '',
            agreementLink: 'персональных данных',
            agreementText: '',
        },
    },
    [QuestionType.PHONE]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.PHONE,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value: ['моб.', 'дом.'][id - 1],
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
            phoneFormatCountry: '+7',
            phoneFormatNumber: '(495) 123-45-67',
        },
    },
    [QuestionType.DATETIME]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.DATETIME,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
            dateFormat: DateFormat[0],
            dateType: DateTimeType.DATE_TIME,
            timeFormat: TimeFormat.TWENTY_FOUR,
        },
    },
    [QuestionType.EMAIL]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.EMAIL,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
        },
    },
    [QuestionType.NAME]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.NAME,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        cols: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            name: ['Фамилия', 'Имя', 'Отчество'][id - 1],
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
        },
    },
    [QuestionType.RESIDENCE]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.RESIDENCE,
        rows: [
            {
                ...cloneDeep(initialRow),
                ...ROW_COUNTRY,
            },
        ],
        params: {
            ...cloneDeep(initialQuestion.params),
            residence: ResidenceType.COUNTRY,
        },
    },
    [QuestionType.NESTED_LIST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.NESTED_LIST,
        rows: [
            {
                ...cloneDeep(initialRow),
                value: 'Уровень 1',
                value3: 1,
                order: 1,
            },
            {
                ...cloneDeep(initialRow),
                value: 'Уровень 2',
                value3: 2,
                order: 2,
            },
            {
                ...cloneDeep(initialRow),
                value: 'Уровень 3',
                value3: 3,
                order: 3,
            },
        ],
    },
    [QuestionType.FILE_UPLOAD]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.FILE_UPLOAD,
        params: {
            ...cloneDeep(initialQuestion.params),
            fileAmount: 1,
            uploadButtonName: 'Загрузить файл',
            fileType: FileUploadType.ANY,
            fileLimitExt: [],
        },
    },
    [QuestionType.GROUP_FREE_ANSWERS]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.GROUP_FREE_ANSWERS,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
    },
    [QuestionType.NUMBER]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.NUMBER,
    },
    [QuestionType.LONG_TEXT]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.LONG_TEXT,
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
            minLength: 0,
            maxLength: DEFAULT_LONG_TEXT_MAX_LENGTH,
            placeholder: PLACEHOLDER.ANSWER,
        },
    },
    [QuestionType.SHORT_TEXT]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.SHORT_TEXT,
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
            minLength: 0,
            maxLength: DEFAULT_SHORT_TEXT_MAX_LENGTH,
            placeholder: PLACEHOLDER.ANSWER,
        },
    },
    [QuestionType.SEM_DIFF]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.SEM_DIFF,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            name: `${id - 3}`,
            type: AnswerType.RADIO,
        })),
    },
    [QuestionType.MATRIX_TEXT_ANSWER]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.MATRIX_TEXT_ANSWER,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            type: AnswerType.TEXT,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            answerType: 'any',
        },
    },
    [QuestionType.MATRIX_FEW_ANSWERS]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.MATRIX_FEW_ANSWERS,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            type: AnswerType.CHECKBOX,
        })),
    },
    [QuestionType.MATRIX_SINGLE_ANSWER]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.MATRIX_SINGLE_ANSWER,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            type: AnswerType.RADIO,
        })),
    },
    [QuestionType.RANK]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.RANK,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
    },
    [QuestionType.TEST_RANK]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.TEST_RANK,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            randomOrder: true,
            testScore: 1,
            testAnswerCorrectText: TEXTS.CORRECT_ANSWER,
            testAnswerIncorrectText: TEXTS.INCORRECT_ANSWER,
        },
    },
    [QuestionType.NPS]: {
        ...cloneDeep(initialQuestion),
        name: '&lt;p&gt;Пожалуйста, оцените по шкале от 0 до 10, насколько вероятно, что вы порекомендуете нас другу или коллеге?&lt;/p&gt;',
        type: QuestionType.NPS,
        params: {
            ...cloneDeep(initialQuestion.params),
            size: 1,
            npsLeftLabel: 'Маловероятно',
            npsRightLabel: 'Очень вероятно',
            npsColors: [getColor('blue-nps')],
        },
    },
    [QuestionType.DISTRIBUTE_SCALE]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.DISTRIBUTE_SCALE,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            distributeSurplus: true,
            distributeLabel: 'Распределить',
            distributeScore: DEFAULT_DISTRIBUTE_SCORE,
            distributeUnit: 'баллов',
        },
    },
    [QuestionType.SMILE]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.SMILE,
        rows: smileTypes[0].smiles.map((smile, key) => ({
            ...cloneDeep(initialRow),
            id: key,
            value: smile,
            order: key,
            width: 0,
            height: 0,
            mimeType: null,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            size: 1,
            smilePack: DEFAULT_SMILE_PACK,
            smileType: '5-smile',
        },
    },
    [QuestionType.MATRIX_RATING]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.MATRIX_RATING,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            amount: 5,
            size: 1,
            color: getColor('blue-star'),
        },
    },
    [QuestionType.CSI]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.CSI,
        rows: [1].map((id) => {
            return {
                ...cloneDeep(initialRow),
                id,
                order: id,
                value2: 'Оценка',
                value3: 'Важность данного критерия для вас',
                commentText: 'Текст комментария',
            };
        }),
        cols: [1, 2].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            type: AnswerType.TEXT,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            amount: 10,
            size: 1,
            color: getColor('blue-star'),
            matrixType: 'numbers',
        },
    },
    [QuestionType.STAR]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.STAR,
        params: {
            ...cloneDeep(initialQuestion.params),
            color: getColor('blue-star'),
            size: 2,
            amount: 5,
        },
    },
    [QuestionType.SCALE]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.SCALE,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            numericValue: id,
            name: String(id),
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            size: 1,
            npsLeftLabel: 'Плохо',
            npsRightLabel: 'Отлично',
        },
    },
    [QuestionType.MATRIX_DROPDOWN_LIST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.MATRIX_DROPDOWN_LIST,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            type: AnswerType.SELECT,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            dropdownListType: DropdownType.DIFFERENT,
            answers: [],
        },
    },
    [QuestionType.ONE_OF_LIST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.ONE_OF_LIST,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
    },
    [QuestionType.FEW_OF_LIST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.FEW_OF_LIST,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
    },
    [QuestionType.SELECT_ONE_IMAGE]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.SELECT_ONE_IMAGE,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            imageColumns: 1,
            size: 100,
            imageTextPosition: 'above',
        },
    },
    [QuestionType.SELECT_FEW_IMAGE]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.SELECT_FEW_IMAGE,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            imageColumns: 1,
            size: 100,
            imageTextPosition: 'above',
        },
    },
    [QuestionType.DROPDOWN_LIST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.DROPDOWN_LIST,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
    },
    [QuestionType.AB_TESTING]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.AB_TESTING,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value: `Креатив ${id}`,
            value3: '50',
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            required: null,
            ABType: QuestionParamsModelAbType.IMAGE,
        },
    },
    [QuestionType.FIRST_CLICK]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.FIRST_CLICK,
        params: {
            ...cloneDeep(initialQuestion.params),
            clickCount: 1,
        },
    },
    [QuestionType.TEXT_PERCEPTION]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.TEXT_PERCEPTION,
        params: {
            ...cloneDeep(initialQuestion.params),
            questionText: '',
        },
    },
    [QuestionType.PAIR_COMPARISON]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.PAIR_COMPARISON,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            textBlock: '',
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            pairComparisonType: QuestionParamsModelPairComparisonType.TEXT,
        },
    },
    [QuestionType.CARD_SORTING]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.CARD_SORTING,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            cardSortingType: QuestionParamsCardSortingType.CLOSE,
            randomOrderText: 'Случайный порядок карточек',
        },
        categories: [
            {
                name: 'Категория 1',
                origin: QuestionCategoryOrigin.USER,
            },
            {
                name: 'Категория 2',
                origin: QuestionCategoryOrigin.USER,
            },
        ],
    },
    [QuestionType.MAX_DIFF]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.MAX_DIFF,
        rows: [1, 2, 3, 4].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            help: true,
            helpText: 'Выберите по одному варианту в каждой колонке: наименее важный и наиболее важный',
            blockSize: 3,
            targetRepetitions: 3,
            bestLabel: 'Наиболее важно',
            worstLabel: 'Наименее важно',
        },
    },
    [QuestionType.VIDEO_TEST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.VIDEO_TEST,
        params: {
            ...cloneDeep(initialQuestion.params),
            required: null,
        },
    },
    [QuestionType.FIVE_SEC_TEST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.FIVE_SEC_TEST,
        params: {
            ...cloneDeep(initialQuestion.params),
            required: null,
            fiveSecDuration: 5,
        },
    },
    [QuestionType.FIGMA_TEST]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.FIGMA_TEST,
        params: {
            ...cloneDeep(initialQuestion.params),
            figmaDisplayMode: 'fitWidth',
            figmaTimerSeconds: null,
            figmaRecordScreen: false,
            figmaRecordAudio: false,
            figmaRecordCamera: false,
            required: true,
        },
    },
    [QuestionType.KANO]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.KANO,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value: '',
            value2: '<p>Как вы отнесётесь, если функция есть?</p>',
            value3: '<p>Как вы отнесётесь, если функции нет?</p>',
            commentText: 'Текст комментария',
            isExpanded: true,
        })),
        cols: [1, 2].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            type: AnswerType.RADIO,
            name: '',
            answers: [],
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: false,
            cantRate: false,
        },
    },
    [QuestionType.CONJOINT]: {
        ...cloneDeep(initialQuestion),
        type: QuestionType.CONJOINT,
        params: {
            ...cloneDeep(initialQuestion.params),
            randomOrderText: 'Случайный порядок карточек',
            randomOrder: true,
            randomScreensOrder: true,
        },
    },
};

export const fillPresetColors = (type: QuestionType, colorPreset?: Array<string>) => {
    if ([QuestionType.CSI, QuestionType.MATRIX_RATING].includes(type)) {
        return { matrixNumbersColor: colorPreset };
    } else if (type === QuestionType.NPS) {
        return { npsColors: colorPreset };
    } else if (type === QuestionType.SCALE) {
        return { scaleColors: colorPreset };
    }
    return {};
};
